// ============================================================
// fondos/legible.js — EL TOPE DE LEGIBILIDAD de una textura (PURO: sin DOM ni imports)
//
// Una textura (`fondos/texturas.js`) pinta su color, con un alfa, sobre el fondo de la sección, y
// el texto de la sección va encima. Si ese color se acerca al del texto, el texto se lee peor: el
// glow claro de un overlay detrás de un titular blanco, o el mesh que la regla L10 pinta con el
// color del TEXTO cuando el acento es el fondo de la sección (`fondos/efectiva.js`). Hasta el
// 03-10-2026 nadie lo miraba, y con el alfa recalibrado del 01-10 (a7ada2c6) algunas secciones
// ya publicadas bajaban de 6 a 2,5:1. Este módulo dice HASTA DÓNDE puede llegar el alfa.
//
// MÓDULO PURO: ES2019 sin `?.` ni `??`; lo importan el runtime y los tests (node).
// ============================================================

/**
 * El SUELO: hasta dónde puede bajar una textura el contraste del texto de la sección, medido contra
 * el color de ese texto y el color liso de la sección. Es 9 y no 4,5 porque lo que se lee no es eso:
 * los párrafos van más apagados que el color de la sección y un overlay va ENCIMA de un fondo animado
 * que ya se ha comido parte del contraste (y que aquí no se puede medir: el `alfaMax` del catálogo es
 * el de cada partícula, no lo que cubre). Calibrado el 03-10-2026 con 100 bloques con textura y 35 con
 * overlay de test222 y sus páginas publicadas: con 9, ningún titular baja de 3 ni ningún párrafo de
 * 4,5 (o del 90 % de lo que tenía sin la textura), y la textura se sigue viendo como con a7ada2c6.
 */
export const SUELO = 9;

/** Si la sección ya estaba por debajo del suelo, la textura no le quita más de esto (10 %). */
export const PERDIDA_MAX = 0.9;

/**
 * Sobre una FOTO no hay un color de fondo con el que medir: el alfa efectivo de una textura no pasa
 * del mayor que tenía un overlay antes del 01-10-2026 (glow oscuro al 35 %), con el que la foto
 * conservaba ≥ 89 % de su detalle (el criterio de Q2, `fondos/capacidad.js`).
 */
export const ALFA_MAX_SOBRE_FOTO = 0.35;

function _lineal(c) {
    const x = c / 255;
    return x <= 0.04045 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
}

/** Luminancia relativa WCAG de `[r, g, b]` (0-255). */
export function luminancia(rgb) {
    return 0.2126 * _lineal(rgb[0]) + 0.7152 * _lineal(rgb[1]) + 0.0722 * _lineal(rgb[2]);
}

/** Contraste WCAG (1-21) entre dos colores `[r, g, b]`. */
export function contraste(a, b) {
    const la = luminancia(a);
    const lb = luminancia(b);
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** `color` al `alfa` (0-1) sobre `fondo`, como lo compone el navegador (`color-mix(in srgb, …)`). */
export function mezcla(fondo, color, alfa) {
    return [0, 1, 2].map(function (k) { return fondo[k] * (1 - alfa) + color[k] * alfa; });
}

/**
 * El alfa MÁXIMO (0-1) con que `color` puede pintarse sobre `fondo` sin que el texto `texto` baje
 * de su suelo: `SUELO`, o el 90 % de lo que tenía si la sección ya estaba por debajo (el suelo es
 * continuo: una sección peor nunca admite más textura que una mejor). Se recorre en pasos de
 * 0,01 porque la luminancia de la mezcla no siempre es monótona (cada canal va por su lado).
 * Sin los tres colores no hay nada que medir: 1, sin tope.
 *
 * @param {number[]|null} fondo - `[r, g, b]` del fondo de la sección
 * @param {number[]|null} texto - `[r, g, b]` del texto de la sección
 * @param {number[]|null} color - `[r, g, b]` de la textura
 * @returns {number}
 */
export function alfaMaxLegible(fondo, texto, color) {
    if (!fondo || !texto || !color) return 1;
    const suelo = Math.min(SUELO, contraste(texto, fondo) * PERDIDA_MAX);
    for (let i = 1; i <= 100; i++) {
        if (contraste(texto, mezcla(fondo, color, i / 100)) < suelo) return (i - 1) / 100;
    }
    return 1;
}

/**
 * La opacidad (0-100) a la que hay que dejar una capa que pinta con `alfaTabla` (0-100, el alfa de
 * su color a opacidad 100) para que su alfa efectivo no pase de `alfaMax` (0-1). `opacidad`
 * ausente = 100. Devuelve la opacidad de entrada si ya cabe (sin tocarla: `undefined` sigue siendo
 * `undefined`), y si no, el tope redondeado hacia abajo.
 */
export function opacidadLegible(opacidad, alfaTabla, alfaMax) {
    const op = (opacidad === undefined || opacidad === null || !isFinite(Number(opacidad))) ? 100 : Number(opacidad);
    if (!(alfaTabla > 0) || alfaTabla * op / 100 <= alfaMax * 100 + 1e-9) return opacidad;
    return Math.max(0, Math.floor(100 * alfaMax * 100 / alfaTabla));
}
